import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Home, ArrowLeft, Users, Crown, Gift, Beaker,
  Search, MoreVertical, Clock, Zap, AlertTriangle,
  Check, X, CreditCard, RefreshCw, Plus, Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const SUBSCRIPTION_TYPES = {
  demo: { label: "Demo", color: "bg-purple-100 text-purple-800", icon: Beaker },
  trial: { label: "Trial", color: "bg-emerald-100 text-emerald-800", icon: Gift },
  paid: { label: "Paid", color: "bg-blue-100 text-blue-800", icon: Crown },
  none: { label: "None", color: "bg-gray-100 text-gray-800", icon: X }
};

export default function AdminSubscriptionManagement() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  
  // Dialog states
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form states
  const [grantForm, setGrantForm] = useState({
    plan: "starter",
    expiration_days: 30,
    credit_limit: 50,
    notes: ""
  });
  const [convertForm, setConvertForm] = useState({
    target_type: "trial",
    plan: "starter",
    extend_days: 14
  });
  const [extendDays, setExtendDays] = useState(30);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API}/admin/contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract contractors with their profiles
      const contractorList = response.data.contractors || response.data || [];
      setContractors(contractorList);
    } catch (error) {
      toast.error("Failed to load contractors");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantDemo = async () => {
    if (!selectedContractor) return;
    setProcessing(true);
    
    try {
      await axios.post(
        `${API}/admin/contractor/${selectedContractor.user_id}/grant-demo`,
        grantForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Demo access granted to ${selectedContractor.email || selectedContractor.name}`);
      setShowGrantDialog(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant demo access");
    } finally {
      setProcessing(false);
    }
  };

  const handleConvertSubscription = async () => {
    if (!selectedContractor) return;
    setProcessing(true);
    
    try {
      await axios.post(
        `${API}/admin/contractor/${selectedContractor.user_id}/convert-subscription`,
        convertForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Subscription converted to ${convertForm.target_type}`);
      setShowConvertDialog(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert subscription");
    } finally {
      setProcessing(false);
    }
  };

  const handleExtendAccess = async () => {
    if (!selectedContractor) return;
    setProcessing(true);
    
    try {
      await axios.post(
        `${API}/admin/contractor/${selectedContractor.user_id}/extend-access?extend_days=${extendDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Extended access by ${extendDays} days`);
      setShowExtendDialog(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend access");
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeSubscription = async (contractor) => {
    if (!confirm(`Are you sure you want to revoke ${contractor.email}'s subscription?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/contractor/${contractor.user_id}/subscription`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscription revoked");
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke subscription");
    }
  };

  const openGrantDialog = (contractor) => {
    setSelectedContractor(contractor);
    setGrantForm({
      plan: "starter",
      expiration_days: 30,
      credit_limit: 50,
      notes: ""
    });
    setShowGrantDialog(true);
  };

  const openConvertDialog = (contractor) => {
    setSelectedContractor(contractor);
    setConvertForm({
      target_type: "trial",
      plan: contractor.profile?.subscription_plan || "starter",
      extend_days: 14
    });
    setShowConvertDialog(true);
  };

  const openExtendDialog = (contractor) => {
    setSelectedContractor(contractor);
    setExtendDays(30);
    setShowExtendDialog(true);
  };

  // Helper to determine subscription type
  const determineSubscriptionType = (profile) => {
    // If explicit type is set, use it
    if (profile.subscription_type && profile.subscription_type !== "none") {
      return profile.subscription_type;
    }
    // Infer from status and plan
    if (profile.subscription_status === "active" && profile.subscription_plan) {
      return "paid";
    }
    return "none";
  };

  // Filter contractors
  const filteredContractors = contractors.filter(c => {
    const profile = c.profile || c;
    const matchesSearch = 
      (c.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (c.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (profile.business_name?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    
    if (filterType === "all") return matchesSearch;
    const type = determineSubscriptionType(profile);
    return matchesSearch && type === filterType;
  });

  const getSubscriptionInfo = (contractor) => {
    const profile = contractor.profile || contractor;
    const type = determineSubscriptionType(profile);
    const status = profile.subscription_status || "inactive";
    const plan = profile.subscription_plan || "";
    
    return { type, status, plan, profile };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-subscription-management">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
            <div className="h-6 border-l border-gray-200" />
            <h1 className="font-manrope font-bold text-xl text-[#1F2933]">
              Subscription Management
            </h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
            <span className="font-manrope font-bold text-[#1F2933] hidden sm:inline">
              Contractor Hub LLC
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Object.entries(SUBSCRIPTION_TYPES).map(([key, config]) => {
            const count = contractors.filter(c => {
              const profile = c.profile || c;
              return determineSubscriptionType(profile) === key;
            }).length;
            const Icon = config.icon;
            
            return (
              <div 
                key={key}
                className={`bg-white rounded-xl p-4 border border-gray-100 cursor-pointer hover:border-gray-200 transition-colors ${filterType === key ? 'ring-2 ring-[#0FA3B1]' : ''}`}
                onClick={() => setFilterType(filterType === key ? "all" : key)}
                data-testid={`filter-${key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2933]">{count}</p>
                    <p className="text-sm text-[#1F2933]/60">{config.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by email, name, or business..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="filter-select">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contractors</SelectItem>
                <SelectItem value="demo">Demo Access</SelectItem>
                <SelectItem value="trial">Free Trial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="none">No Subscription</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchContractors} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Contractors Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contractor</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="spinner mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredContractors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No contractors found
                  </TableCell>
                </TableRow>
              ) : (
                filteredContractors.map((contractor) => {
                  const { type, status, plan, profile } = getSubscriptionInfo(contractor);
                  const typeConfig = SUBSCRIPTION_TYPES[type] || SUBSCRIPTION_TYPES.none;
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <TableRow key={contractor.user_id} data-testid={`contractor-row-${contractor.user_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#1F2933]">
                            {profile.business_name || contractor.name || "No Name"}
                          </p>
                          <p className="text-sm text-gray-500">{contractor.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={typeConfig.color}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                          {status === "active" ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{plan || "—"}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Zap className="w-4 h-4 text-amber-500" />
                          {profile.credit_balance || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        {type === "demo" && profile.demo_expiration ? (
                          <span className="text-sm">{formatDate(profile.demo_expiration)}</span>
                        ) : type === "trial" && profile.trial_start_date ? (
                          <span className="text-sm">
                            {formatDate(new Date(new Date(profile.trial_start_date).getTime() + (profile.trial_duration_days || 14) * 24 * 60 * 60 * 1000))}
                          </span>
                        ) : type === "paid" && profile.subscription_end ? (
                          <span className="text-sm">{formatDate(profile.subscription_end)}</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`actions-${contractor.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openGrantDialog(contractor)}>
                              <Beaker className="w-4 h-4 mr-2" /> Grant Demo Access
                            </DropdownMenuItem>
                            {(type === "demo" || type === "trial") && (
                              <DropdownMenuItem onClick={() => openExtendDialog(contractor)}>
                                <Calendar className="w-4 h-4 mr-2" /> Extend Access
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openConvertDialog(contractor)}>
                              <RefreshCw className="w-4 h-4 mr-2" /> Convert Subscription
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleRevokeSubscription(contractor)}
                              className="text-red-600"
                            >
                              <X className="w-4 h-4 mr-2" /> Revoke Access
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Grant Demo Dialog */}
      <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Beaker className="w-5 h-5 text-purple-600" />
              Grant Demo Access
            </DialogTitle>
            <DialogDescription>
              Grant demo subscription to {selectedContractor?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Plan Level</Label>
              <Select 
                value={grantForm.plan} 
                onValueChange={(v) => setGrantForm({...grantForm, plan: v})}
              >
                <SelectTrigger data-testid="grant-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Duration (days)</Label>
              <Input
                type="number"
                value={grantForm.expiration_days}
                onChange={(e) => setGrantForm({...grantForm, expiration_days: parseInt(e.target.value) || 30})}
                data-testid="grant-days-input"
              />
            </div>
            
            <div>
              <Label>Credit Limit</Label>
              <Input
                type="number"
                value={grantForm.credit_limit}
                onChange={(e) => setGrantForm({...grantForm, credit_limit: parseInt(e.target.value) || 50})}
                data-testid="grant-credits-input"
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={grantForm.notes}
                onChange={(e) => setGrantForm({...grantForm, notes: e.target.value})}
                placeholder="e.g., Demo for investor pitch"
                data-testid="grant-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGrantDemo}
              disabled={processing}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="grant-submit-btn"
            >
              {processing ? <div className="spinner w-4 h-4" /> : "Grant Demo Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Subscription Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Convert Subscription
            </DialogTitle>
            <DialogDescription>
              Change subscription type for {selectedContractor?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Convert To</Label>
              <Select 
                value={convertForm.target_type} 
                onValueChange={(v) => setConvertForm({...convertForm, target_type: v})}
              >
                <SelectTrigger data-testid="convert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="demo">Demo Access</SelectItem>
                  <SelectItem value="trial">Free Trial</SelectItem>
                  <SelectItem value="paid">Paid Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Plan</Label>
              <Select 
                value={convertForm.plan} 
                onValueChange={(v) => setConvertForm({...convertForm, plan: v})}
              >
                <SelectTrigger data-testid="convert-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(convertForm.target_type === "demo" || convertForm.target_type === "trial") && (
              <div>
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={convertForm.extend_days}
                  onChange={(e) => setConvertForm({...convertForm, extend_days: parseInt(e.target.value) || 14})}
                  data-testid="convert-days-input"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertSubscription}
              disabled={processing}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="convert-submit-btn"
            >
              {processing ? <div className="spinner w-4 h-4" /> : "Convert Subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Access Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Extend Access
            </DialogTitle>
            <DialogDescription>
              Extend demo/trial access for {selectedContractor?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Extend by (days)</Label>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                data-testid="extend-days-input"
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <p>This will add {extendDays} days to the current expiration date.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExtendAccess}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="extend-submit-btn"
            >
              {processing ? <div className="spinner w-4 h-4" /> : "Extend Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
